import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle, ArrowsClockwise, Gear, Warning, ArrowRight } from "@phosphor-icons/react";
import CuentaCombobox from "@/components/CuentaCombobox";

const UNIDADES = ["Mostrador", "Logistica", "Mayorista", "Ajustes"];

export default function MovimientosAClasificarPage() {
  const [data, setData] = useState({ ventas: [], compras: [], cobros_pagos: [], cheques_emitidos: [], cheques_recibidos: [], totales: {} });
  const [cuentas, setCuentas] = useState([]);
  const [rowCuenta, setRowCuenta] = useState({});
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [unMapping, setUnMapping] = useState({});
  const [savingCfg, setSavingCfg] = useState(false);
  const [tab, setTab] = useState("ventas");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: cu }, { data: cfg }] = await Promise.all([
        api.get("/contabilidad/movimientos-a-clasificar"),
        api.get("/cuentas"),
        api.get("/contabilidad/config/unidades-negocio"),
      ]);
      setData(p);
      setCuentas(cu || []);
      setUnMapping(cfg?.mapping || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cuentasGasto = (cuentas || []).filter((x) => x.imputable && (x.tipo === "egreso" || x.tipo === "activo"));
  const cuentasIngreso = (cuentas || []).filter((x) => x.imputable && x.tipo === "ingreso");

  const clasificarCompra = async (cid) => {
    const codigo = rowCuenta[cid];
    if (!codigo) return toast.error("Seleccioná una cuenta contable");
    try {
      await api.post(`/contabilidad/compras-a-clasificar/${cid}`, { cuenta_codigo: codigo });
      toast.success("Compra clasificada y asiento generado");
      setRowCuenta({ ...rowCuenta, [cid]: undefined });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const runBackfill = async (force = false) => {
    const msg = force
      ? "Force: además de generar faltantes, va a BORRAR y REGENERAR los asientos automáticos existentes (ventas/compras productos o con cta. default/cobros/pagos con caja OK) con la lógica actualizada.\n\nLos asientos de compras clasificadas manualmente se preservan.\n\n¿Continuar?"
      : "Generar asientos faltantes de ventas, compras (productos o con cuenta default) y cobros/pagos con caja asignada. ¿Continuar?";
    if (!window.confirm(msg)) return;
    setRunning(true);
    try {
      const { data: r } = await api.post(`/contabilidad/backfill-asientos${force ? "?force=true" : ""}`);
      const s = r.stats || {};
      toast.success(
        `OK: v=${s.ventas || 0} c=${s.compras || 0} co=${s.cobros || 0} pa=${s.pagos || 0}${force ? ` · regen=${s.regenerados || 0}` : ""}`
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en backfill");
    } finally {
      setRunning(false);
    }
  };

  const runBackfillMovs = async () => {
    if (!window.confirm("Genera asientos por doble partida de todos los movimientos de caja ya clasificados (con caja que tenga cuenta contable). ¿Continuar?")) return;
    setRunning(true);
    try {
      const { data: r } = await api.post("/contabilidad/backfill-movs-clasificados");
      const s = r.stats || {};
      toast.success(`Movs clasificados asentados: ${s.generados || 0} (skipped ${s.skipped || 0}, sin cta ${s.sin_caja_cuenta || 0})`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setRunning(false);
    }
  };

  const saveUnMapping = async () => {
    setSavingCfg(true);
    try {
      await api.put("/contabilidad/config/unidades-negocio", { mapping: unMapping });
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSavingCfg(false);
    }
  };

  const t = data.totales || {};
  const totalPend = (t.ventas || 0) + (t.compras || 0) + (t.cobros_pagos || 0) + (t.cheques_emitidos || 0) + (t.cheques_recibidos || 0);

  return (
    <>
      <Topbar
        title="Movimientos a Clasificar"
        subtitle="Ventas, compras, cobros, pagos y cheques pendientes de generar asiento contable."
        right={
          <div className="flex items-center gap-2">
            <Button
              onClick={() => runBackfill(false)}
              disabled={running}
              variant="outline"
              className="rounded-sm h-9 border-[#6B1F24] text-[#6B1F24] hover:bg-[#F5F1E9]"
              data-testid="backfill-asientos-btn"
            >
              <ArrowsClockwise size={16} className="mr-1" />
              {running ? "Procesando…" : "Generar faltantes"}
            </Button>
            <Button
              onClick={() => runBackfill(true)}
              disabled={running}
              className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519]"
              data-testid="backfill-force-btn"
            >
              <ArrowsClockwise size={16} className="mr-1" />
              Reasentar todo (force)
            </Button>
          </div>
        }
      />
      <div className="p-4 space-y-4">
        {/* Panel: Cuenta de ingreso por unidad de negocio */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-neutral-50 flex items-center gap-2">
            <Gear size={16} className="text-[#6B1F24]" />
            <span className="text-[11px] uppercase tracking-widest font-mono text-[#6B1F24] font-semibold">
              Cuenta de ingreso por unidad de negocio
            </span>
            <span className="text-[11px] text-neutral-500 ml-auto">
              Fallback si el cliente no tiene cuenta específica
            </span>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {UNIDADES.map((un) => (
              <div key={un}>
                <div className="text-[10px] uppercase tracking-widest font-mono text-neutral-500 mb-1">{un}</div>
                <CuentaCombobox
                  value={unMapping[un] || ""}
                  onChange={(codigo) => setUnMapping({ ...unMapping, [un]: codigo })}
                  cuentas={cuentasIngreso}
                  placeholder="4.1.1 Venta de Bienes (default)"
                  testid={`cfg-un-${un}`}
                  compact
                />
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t bg-neutral-50 flex justify-end">
            <Button size="sm" onClick={saveUnMapping} disabled={savingCfg}
              className="rounded-sm h-8 bg-[#6B1F24] hover:bg-[#571519]" data-testid="cfg-un-save">
              {savingCfg ? "Guardando…" : "Guardar configuración"}
            </Button>
          </div>
        </div>

        {/* Contenido */}
        {loading ? (
          <div className="text-neutral-500 text-sm">Cargando…</div>
        ) : totalPend === 0 ? (
          <div className="border border-neutral-200 bg-white rounded-sm p-8 text-center">
            <CheckCircle size={32} weight="fill" className="text-emerald-500 mx-auto mb-2" />
            <div className="font-heading text-lg">Todo asentado</div>
            <div className="text-[13px] text-neutral-500 mt-1">No hay movimientos pendientes de clasificación contable.</div>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="ventas" data-testid="tab-ventas">Ventas <span className="ml-1 text-[10px] font-mono text-neutral-500">{t.ventas || 0}</span></TabsTrigger>
              <TabsTrigger value="compras" data-testid="tab-compras">Compras <span className="ml-1 text-[10px] font-mono text-neutral-500">{t.compras || 0}</span></TabsTrigger>
              <TabsTrigger value="cobros_pagos" data-testid="tab-cobros-pagos">Cobros/Pagos <span className="ml-1 text-[10px] font-mono text-neutral-500">{t.cobros_pagos || 0}</span></TabsTrigger>
              <TabsTrigger value="cheques" data-testid="tab-cheques">Cheques <span className="ml-1 text-[10px] font-mono text-neutral-500">{(t.cheques_emitidos || 0) + (t.cheques_recibidos || 0)}</span></TabsTrigger>
            </TabsList>

            {/* VENTAS */}
            <TabsContent value="ventas" className="mt-4">
              {(data.ventas || []).length === 0 ? (
                <div className="text-[13px] text-neutral-500">Sin ventas pendientes.</div>
              ) : (
                <>
                  <div className="text-[12px] text-neutral-600 mb-2">
                    Estas ventas todavía no tienen asiento. Tocá <b>"Generar faltantes"</b> arriba para asentarlas automáticamente (usa la prioridad cliente → unidad de negocio → default).
                  </div>
                  <TablaSimple rows={data.ventas} cols={[
                    { key: "fecha", label: "Fecha", w: 24, cls: "font-mono", val: (r) => fmtDate(r.fecha) },
                    { key: "comp", label: "Comprobante", w: 40, cls: "font-mono text-[12px]", val: (r) => `${r.tipo_comprobante || ""} ${r.numero || ""}` },
                    { key: "cli", label: "Cliente", val: (r) => r.contraparte_nombre },
                    { key: "un", label: "Unidad", w: 28, val: (r) => r.unidad_negocio || "—" },
                    { key: "total", label: "Total", w: 36, cls: "text-right font-mono", val: (r) => fmtMoney(r.total, r.moneda) },
                  ]} />
                </>
              )}
            </TabsContent>

            {/* COMPRAS */}
            <TabsContent value="compras" className="mt-4">
              {(data.compras || []).length === 0 ? (
                <div className="text-[13px] text-neutral-500">Sin compras pendientes.</div>
              ) : (
                <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
                  <table className="w-full text-[13px]">
                    <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50">
                      <tr>
                        <th className="px-4 py-2 text-left w-24">Fecha</th>
                        <th className="px-4 py-2 text-left w-40">Comprobante</th>
                        <th className="px-4 py-2 text-left">Proveedor</th>
                        <th className="px-4 py-2 text-left w-28">Tipo</th>
                        <th className="px-4 py-2 text-right w-36">Total</th>
                        <th className="px-4 py-2 text-left w-72">Cuenta contable</th>
                        <th className="px-4 py-2 text-left w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.compras.map((r) => (
                        <tr key={r.id} className="border-t hover:bg-neutral-50" data-testid={`clasif-row-${r.id}`}>
                          <td className="px-4 py-2 font-mono">{fmtDate(r.fecha)}</td>
                          <td className="px-4 py-2 font-mono text-[12px]">{r.tipo_comprobante} {r.numero}</td>
                          <td className="px-4 py-2">{r.contraparte_nombre}</td>
                          <td className="px-4 py-2 text-[11px] uppercase font-mono text-neutral-500">{r.tipo_proveedor}</td>
                          <td className="px-4 py-2 text-right font-mono">{fmtMoney(r.total, r.moneda)}</td>
                          <td className="px-4 py-2">
                            <CuentaCombobox
                              value={rowCuenta[r.id] || ""}
                              onChange={(codigo) => setRowCuenta({ ...rowCuenta, [r.id]: codigo })}
                              cuentas={cuentasGasto}
                              compact
                              testid={`clasif-cuenta-${r.id}`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <Button size="sm" disabled={!rowCuenta[r.id]} onClick={() => clasificarCompra(r.id)}
                              className="rounded-sm h-8 bg-[#6B1F24] hover:bg-[#571519]"
                              data-testid={`clasif-btn-${r.id}`}>
                              Clasificar
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* COBROS / PAGOS */}
            <TabsContent value="cobros_pagos" className="mt-4">
              {(data.cobros_pagos || []).length === 0 ? (
                <div className="text-[13px] text-neutral-500">Sin cobros/pagos pendientes.</div>
              ) : (
                <>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-[12px] text-amber-900 mb-3 flex gap-2 items-start">
                    <Warning size={16} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      Estos cobros y pagos no tienen asiento porque la <b>caja asociada no tiene cuenta contable asignada</b>. Vinculá la cuenta desde <Link to="/cajas" className="underline">Caja y Bancos</Link> y volvé a tocar <b>"Generar faltantes"</b>.
                    </div>
                  </div>
                  <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
                    <table className="w-full text-[13px]">
                      <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50">
                        <tr>
                          <th className="px-4 py-2 text-left w-24">Fecha</th>
                          <th className="px-4 py-2 text-left w-16">Tipo</th>
                          <th className="px-4 py-2 text-left">Contraparte</th>
                          <th className="px-4 py-2 text-left w-40">Caja</th>
                          <th className="px-4 py-2 text-right w-36">Monto</th>
                          <th className="px-4 py-2 text-left w-40">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.cobros_pagos.map((r) => (
                          <tr key={r.id} className="border-t hover:bg-neutral-50" data-testid={`cp-row-${r.id}`}>
                            <td className="px-4 py-2 font-mono">{fmtDate(r.fecha)}</td>
                            <td className="px-4 py-2 uppercase text-[11px] font-mono">{r.tipo}</td>
                            <td className="px-4 py-2">{r.contraparte_nombre}</td>
                            <td className="px-4 py-2">{r.caja_nombre}</td>
                            <td className="px-4 py-2 text-right font-mono">{fmtMoney(r.monto, r.moneda)}</td>
                            <td className="px-4 py-2">
                              {r.caja_tiene_cuenta ? (
                                <span className="text-[11px] text-emerald-700 font-mono uppercase">Caja OK</span>
                              ) : (
                                <span className="text-[11px] text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-sm font-mono uppercase">Sin cta. caja</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </TabsContent>

            {/* CHEQUES */}
            <TabsContent value="cheques" className="mt-4 space-y-4">
              {(data.cheques_emitidos || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest font-mono text-[#6B1F24] font-semibold mb-2">
                    Cheques Emitidos pendientes
                  </div>
                  <TablaSimple rows={data.cheques_emitidos} cols={[
                    { key: "fecha", label: "Emisión", w: 24, cls: "font-mono", val: (r) => fmtDate(r.fecha_emision) },
                    { key: "num", label: "Nº", w: 24, cls: "font-mono", val: (r) => r.numero },
                    { key: "prov", label: "Proveedor", val: (r) => r.proveedor_nombre },
                    { key: "cobro", label: "Pago", w: 24, cls: "font-mono", val: (r) => fmtDate(r.fecha_cobro) },
                    { key: "monto", label: "Monto", w: 36, cls: "text-right font-mono", val: (r) => fmtMoney(r.monto, r.moneda) },
                  ]} />
                </div>
              )}
              {(data.cheques_recibidos || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest font-mono text-[#6B1F24] font-semibold mb-2">
                    Cheques Recibidos pendientes
                  </div>
                  <TablaSimple rows={data.cheques_recibidos} cols={[
                    { key: "fecha", label: "Recepción", w: 24, cls: "font-mono", val: (r) => fmtDate(r.fecha_recepcion) },
                    { key: "num", label: "Nº", w: 24, cls: "font-mono", val: (r) => r.numero },
                    { key: "cli", label: "Cliente", val: (r) => r.cliente_nombre },
                    { key: "cobro", label: "Cobro", w: 24, cls: "font-mono", val: (r) => fmtDate(r.fecha_cobro) },
                    { key: "monto", label: "Monto", w: 36, cls: "text-right font-mono", val: (r) => fmtMoney(r.monto, r.moneda) },
                  ]} />
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Herramientas avanzadas */}
        <div className="border border-dashed border-neutral-300 rounded-sm p-3 flex items-center justify-between">
          <div className="text-[11px] text-neutral-500 font-mono">
            Otras herramientas contables
          </div>
          <Button size="sm" variant="outline" onClick={runBackfillMovs} disabled={running}
            className="rounded-sm h-8 text-[11px]" data-testid="backfill-movs-btn">
            <ArrowRight size={12} className="mr-1" />
            Asentar movs de cajas clasificados
          </Button>
        </div>
      </div>
    </>
  );
}

function TablaSimple({ rows, cols }) {
  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
      <table className="w-full text-[13px]">
        <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50">
          <tr>
            {cols.map((c) => (
              <th key={c.key} className={`px-4 py-2 text-left ${c.cls?.includes("text-right") ? "text-right" : ""}`} style={c.w ? { width: `${c.w * 4}px` } : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t hover:bg-neutral-50">
              {cols.map((c) => (
                <td key={c.key} className={`px-4 py-2 ${c.cls || ""}`}>
                  {c.val(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
